/**
 * Helpers para apresentação de canais do chat — em especial DMs, que precisam
 * ser exibidas com o nome do OUTRO participante (não com o ID `dm-uid1-uid2`)
 * e filtradas para que cada usuário só veja as conversas das quais participa.
 */

import type { ChatChannelData, ClinicMemberData } from '@/services/firebase/chat';

/**
 * Retorna `true` quando o canal deve aparecer na lista do `currentUserId`.
 * - Canais normais (qualquer `type !== 'dm'`): sempre visíveis para membros da clínica.
 * - DMs (`type === 'dm'`): visíveis SÓ se o usuário é um dos dois participantes.
 */
export function isVisibleChannel(channel: ChatChannelData, currentUserId: string): boolean {
  if (channel.type !== 'dm') return true;
  return channel.members?.[currentUserId] === true;
}

/**
 * Retorna o nome para exibir no header / sidebar.
 * - Canais normais: o próprio `name`.
 * - DMs: nome do outro participante (lido de `dmParticipants`, com fallback
 *   no map `clinicMembers` que vem de `chat/{clinic}/members`).
 */
export function getChannelDisplayName(
  channel: ChatChannelData,
  currentUserId: string,
  clinicMembers?: Record<string, string>,
): string {
  if (channel.type !== 'dm') return channel.name;

  const otherUid = Object.keys(channel.members || {}).find((uid) => uid !== currentUserId);
  if (!otherUid) return 'Conversa privada';

  if (channel.dmParticipants?.[otherUid]) return channel.dmParticipants[otherUid];
  if (clinicMembers?.[otherUid]) return clinicMembers[otherUid];
  return 'Conversa privada';
}

/**
 * Converte uma lista de membros da clínica em um lookup `uid → name`.
 */
export function membersToLookup(members: ClinicMemberData[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const m of members) map[m.id] = m.name;
  return map;
}
